import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fecha, fechaHora } from "../lib/formato";
import { Aviso, Cabecera, Cargando, Modal, Panel, TarjetaDato, Vacio } from "../componentes/piezas";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";

interface Version {
  id: string;
  numero: number;
  publicadaEn: string;
  usuario: { nombre: string } | null;
  socios: number;
}

interface Contrato {
  vigente: (Version & { texto: string }) | null;
  versiones: Version[];
  huecos: Record<string, string>;
  sinFirmar: number;
}

interface Pendiente {
  id: string;
  numeroSocio: number;
  nombre: string;
  apellidos: string;
  fechaAlta: string;
}

/**
 * El contrato de socio del club: la declaración jurada y las normas de
 * convivencia que van en la hoja de alta.
 *
 * Se escribe una vez, con huecos entre llaves que se rellenan solos con los
 * datos de cada socio. Cambiarlo no pisa lo anterior: nace una versión nueva y
 * la vieja se queda, porque quien firmó en su día firmó **aquel** texto y esa
 * es la única forma de poder demostrar a qué se comprometió.
 */
export default function Contrato() {
  const { t, cuenta } = useIdioma();
  const { puede } = useSesion();
  const clienteConsultas = useQueryClient();
  const [texto, setTexto] = useState("");
  const [tocado, setTocado] = useState(false);
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [viendo, setViendo] = useState<number | null>(null);

  const datos = useQuery({ queryKey: ["contrato"], queryFn: () => api<Contrato>("/contrato") });
  const pendientes = useQuery({
    queryKey: ["contrato-pendientes"],
    queryFn: () => api<{ hayContrato: boolean; socios: Pendiente[] }>("/contrato/pendientes"),
  });

  // El texto guardado entra en el cuadro una sola vez; a partir de ahi manda
  // lo que este escribiendo, que si no se le borraria al refrescarse la lista.
  useEffect(() => {
    if (!tocado && datos.data) setTexto(datos.data.vigente?.texto ?? "");
  }, [datos.data, tocado]);

  async function guardar() {
    setError("");
    setMensaje("");
    setGuardando(true);
    try {
      const r = await api<{ nueva: boolean; version: { numero: number }; huecosRaros?: string[] }>("/contrato", {
        metodo: "PUT",
        cuerpo: { texto },
      });
      setTocado(false);
      void clienteConsultas.invalidateQueries({ queryKey: ["contrato"] });
      void clienteConsultas.invalidateQueries({ queryKey: ["contrato-pendientes"] });

      if (!r.nueva) setMensaje(t("co2.sinCambios", { numero: r.version.numero }));
      else if (r.huecosRaros?.length) {
        setMensaje(
          t("co2.guardadoConRaros", {
            numero: r.version.numero,
            huecos: r.huecosRaros.map((h) => `{{${h}}}`).join(", "),
          }),
        );
      } else setMensaje(t("co2.guardado", { numero: r.version.numero }));
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("co2.fallo"));
    } finally {
      setGuardando(false);
    }
  }

  if (datos.isLoading) return <Cargando />;
  const contrato = datos.data;
  const puedeEditar = puede("ajustesClub");
  const faltan = pendientes.data?.socios ?? [];

  return (
    <>
      <Cabecera
        titulo={t("co2.titulo")}
        descripcion={t("co2.descripcion")}
      >
        {puedeEditar && (
          <button className="boton" disabled={guardando || texto.trim().length < 20} onClick={guardar}>
            {guardando ? t("g.guardando") : t("g.guardar")}
          </button>
        )}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>
      <Aviso tipo="ok">{mensaje}</Aviso>

      {!contrato?.vigente && (
        <Aviso tipo="atencion">
          {t("co2.sinEscribir")}
        </Aviso>
      )}

      <div className="rejilla tres" style={{ marginBottom: 16 }}>
        <TarjetaDato
          etiqueta={t("co2.versionActual")}
          valor={contrato?.vigente ? t("co2.numeroVersion", { numero: contrato.vigente.numero }) : "—"}
          pie={
            contrato?.vigente
              ? t("co2.publicada", { fecha: fecha(contrato.vigente.publicadaEn) })
              : t("co2.sinEscribirPie")
          }
        />
        <TarjetaDato
          etiqueta={t("co2.loHanFirmado")}
          valor={String(contrato?.versiones.reduce((s, v) => s + v.socios, 0) ?? 0)}
          pie={t("co2.contandoVersiones")}
        />
        <TarjetaDato
          etiqueta={t("co2.sinFirmar")}
          valor={String(contrato?.sinFirmar ?? 0)}
          pie={contrato?.sinFirmar ? t("co2.faltanDeAlta") : t("co2.ningunoPendiente")}
        />
      </div>

      <div className="rejilla contrato">
        <Panel titulo={puedeEditar ? t("co2.elTexto") : t("co2.elTextoDeHoy")}>
          <textarea
            className="editor-contrato"
            value={texto}
            readOnly={!puedeEditar}
            onChange={(e) => {
              setTexto(e.target.value);
              setTocado(true);
            }}
            placeholder={t("co2.ejemplo")}
            spellCheck
          />
          {puedeEditar && (
            <p className="suave pequeno" style={{ margin: "10px 0 0" }}>
              {t("co2.seGuardaTalCual")}
            </p>
          )}
        </Panel>

        <div className="columna" style={{ gap: 16 }}>
          <Panel titulo={t("co2.huecos")}>
            <p className="suave pequeno" style={{ marginTop: 0 }}>
              {t("co2.huecosAyuda")}
            </p>
            <ul className="lista-huecos">
              {Object.entries(contrato?.huecos ?? {}).map(([clave, que]) => (
                <li key={clave}>
                  <code>{`{{${clave}}}`}</code>
                  <span className="suave pequeno">{que}</span>
                </li>
              ))}
            </ul>
          </Panel>

          <Panel titulo={t("co2.versiones")} sinCuerpo>
            {(contrato?.versiones.length ?? 0) === 0 ? (
              <Vacio>{t("co2.sinVersiones")}</Vacio>
            ) : (
              <div className="tabla-envoltorio">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>{t("co2.numero")}</th>
                      <th>{t("co2.publicadaColumna")}</th>
                      <th className="num">{t("co2.firmas")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {contrato!.versiones.map((v) => (
                      <tr key={v.id}>
                        <td className="tabular">{v.numero}</td>
                        <td className="pequeno">
                          {fechaHora(v.publicadaEn)}
                          <div className="suave pequeno">{v.usuario?.nombre ?? "—"}</div>
                        </td>
                        <td className="num tabular">{v.socios}</td>
                        <td>
                          <button className="boton fantasma" onClick={() => setViendo(v.numero)}>
                            {t("co2.ver")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      </div>

      {contrato?.vigente && faltan.length > 0 && (
        <Panel titulo={t("co2.sinFirmarCuantos", { n: faltan.length })} sinCuerpo>
          <div className="tabla-envoltorio" style={{ maxHeight: 380, overflowY: "auto" }}>
            <table className="tabla">
              <thead>
                <tr>
                  <th>{t("co2.numero")}</th>
                  <th>{t("co2.socioColumna")}</th>
                  <th>{t("co2.deAltaDesde")}</th>
                </tr>
              </thead>
              <tbody>
                {faltan.map((s) => (
                  <tr key={s.id}>
                    <td className="tabular">{s.numeroSocio}</td>
                    <td>
                      <Link to={`/socios/${s.id}`}>
                        {s.nombre} {s.apellidos}
                      </Link>
                    </td>
                    <td className="pequeno">{fecha(s.fechaAlta)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="suave pequeno" style={{ padding: "10px 16px", margin: 0 }}>
            {cuenta(faltan.length, "co2.unSocioDeAntes", "co2.sociosDeAntes")} {t("co2.seFirmaDesde")}
          </p>
        </Panel>
      )}

      {viendo !== null && <DialogoVersion numero={viendo} alCerrar={() => setViendo(null)} />}
    </>
  );
}

/** Una versión anterior, tal cual se publicó. */
function DialogoVersion({ numero, alCerrar }: { numero: number; alCerrar: () => void }) {
  const { t } = useIdioma();
  const version = useQuery({
    queryKey: ["contrato-version", numero],
    queryFn: () => api<{ texto: string; publicadaEn: string }>(`/contrato/versiones/${numero}`),
  });

  return (
    <Modal
      titulo={t("co2.v.titulo", { numero })}
      alCerrar={alCerrar}
      acciones={
        <button className="boton" onClick={alCerrar}>
          {t("g.cerrar")}
        </button>
      }
    >
      {version.isLoading ? (
        <Cargando />
      ) : (
        <>
          <p className="suave pequeno">
            {t("co2.v.publicadaEl", { fecha: fechaHora(version.data!.publicadaEn) })}
          </p>
          <pre className="texto-contrato">{version.data!.texto}</pre>
        </>
      )}
    </Modal>
  );
}
